import React from "react";
import { Reveal } from "./Reveal";
import { Quote, ArrowUpRight, BadgeCheck, Languages, Clock, Mail } from "lucide-react";
import { SITE, IMAGES, CREDENTIALS, APPROACH, ABOUT_ME } from "../data/site";

export const AboutTherapist = ({ onBook }) => (
  <section id="about" className="py-24 sm:py-32">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
        <div className="relative">
          <div className="absolute -inset-4 rounded-[32px] bg-[#F7EBC6]/50 -rotate-3" />
          <img src={IMAGES.nook} alt="Pratyusha's counselling room" className="relative rounded-[28px] w-full h-[480px] object-cover soft-shadow border border-[#C9DFAC]" />
          <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-[#3A7D32] text-[#F2F8E8] rounded-2xl p-5 w-56 soft-shadow">
            <div className="font-serif text-2xl leading-tight">RCI Licensed</div>
            <div className="text-xs text-[#FFC9A0] mt-1">MPhil Clinical Psychology · MSc Psychology</div>
          </div>
        </div>
      </Reveal>

      <div className="lg:col-span-7">
        <Reveal><span className="overline">01 — Meet your therapist</span></Reveal>
        <Reveal delay={0.05}>
          <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-[#20301A]">
            {SITE.therapist}
          </h2>
          <p className="text-[#55703F] mt-2 text-sm font-mono uppercase tracking-widest">{SITE.tagline}</p>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-5 flex flex-wrap gap-2">
            {CREDENTIALS.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full bg-[#DFF0C4] border border-[#C9DFAC] px-3.5 py-1.5 text-xs text-[#20301A]">
                <BadgeCheck size={13} className="text-[#3A7D32]" /> {c}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mt-8 pl-6 border-l-2 border-[#D98BA0]">
            <Quote className="absolute -left-3 -top-2 text-[#D98BA0]" size={22} />
            <p className="font-serif italic text-xl sm:text-2xl text-[#3A7D32] leading-snug">
              "Let's explore the patterns, pain, and possibilities — together, with compassion and clarity."
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="mt-7 text-[#55703F] leading-relaxed max-w-xl">
            Hi, I'm Pratyusha — a psychologist offering a therapeutic space that is collaborative, affirming and
            grounded in empathy. My approach is person-centred, trauma-informed, and guided by evidence-based
            modalities including CBT, DBT, Schema Therapy, psychodynamic insight work, and — for couples and families —
            the Gottman Method, Family Therapy and Systems Therapy. I work with children, adults and couples,
            tailoring each session to your unique pace — blending structure with flexibility to meet you where you are.
          </p>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="mt-7 flex flex-col sm:flex-row gap-5 items-start max-w-2xl">
            <img src="/therapist.jpg" alt="Pratyusha Nagisetty" data-testid="therapist-photo"
              className="w-56 sm:w-72 aspect-[3/4] object-cover object-top rounded-2xl soft-shadow border-2 border-[#FFC9A0] shrink-0" />
            <div data-testid="about-me-block" className="flex-1 rounded-3xl bg-[#FFEFDF] border border-[#FFC9A0] p-6 sm:p-7">
              <div className="overline text-[#C77D94] mb-3">{ABOUT_ME.kicker}</div>
              {ABOUT_ME.lines.map((line, i) => (
                <p key={i} className={`font-serif italic leading-snug text-[#3C5A2E] ${i === 0 ? "text-xl sm:text-2xl text-[#20301A]" : "text-base sm:text-lg mt-3"}`}>
                  {line}
                </p>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-6">
            <div className="overline mb-3">Therapeutic approach</div>
            <div className="flex flex-wrap gap-2">
              {APPROACH.map((b) => (
                <span key={b} className="rounded-full bg-[#D8E5ED]/60 border border-[#B8D1E0] px-3.5 py-1.5 text-xs text-[#20301A]">{b}</span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.22}>
          <div className="mt-7 grid sm:grid-cols-3 gap-3">
            <div className="rounded-2xl bg-[#FFEFDF] border border-[#FFC9A0] p-4">
              <Clock size={16} className="text-[#3A7D32]" />
              <div className="text-xs text-[#55703F] mt-2 font-mono uppercase tracking-wider">Session</div>
              <div className="text-sm text-[#20301A] font-medium">{SITE.sessionDuration}</div>
            </div>
            <div className="rounded-2xl bg-[#F7EBC6]/60 border border-[#EADB9B] p-4">
              <Languages size={16} className="text-[#3A7D32]" />
              <div className="text-xs text-[#55703F] mt-2 font-mono uppercase tracking-wider">Languages</div>
              <div className="text-sm text-[#20301A] font-medium">{SITE.languages.join(", ")}</div>
            </div>
            <a href={`mailto:${SITE.email}`} className="rounded-2xl bg-[#DFF0C4] border border-[#C9DFAC] p-4 hover-lift block">
              <Mail size={16} className="text-[#3A7D32]" />
              <div className="text-xs text-[#55703F] mt-2 font-mono uppercase tracking-wider">Email</div>
              <div className="text-sm text-[#20301A] font-medium break-all">{SITE.email}</div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.26}>
          <button data-testid="about-book-cta" onClick={onBook}
            className="group mt-9 inline-flex items-center gap-2 text-[#3A7D32] font-medium text-sm border-b-2 border-[#3A7D32] pb-1 hover:gap-3 transition-all">
            Begin your journey <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);
